import React, { useMemo, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppInput } from '../../components/AppInput';
import { AppScreen } from '../../components/AppScreen';
import { ScreenHeader } from '../../components/ScreenHeader';
import type { ApiError } from '../../api/apiError';
import type { RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';

type Props = NativeStackScreenProps<RootStackParamList, 'ChangePassword'>;

export function validateNewPassword(value: string): string | null {
  if (value.length < 8 || value.length > 128) return 'Use 8 to 128 characters.';
  if (!/[A-Z]/.test(value) || !/[a-z]/.test(value) || !/[0-9]/.test(value) || !/[^A-Za-z0-9]/.test(value)) {
    return 'Include uppercase, lowercase, number, and special character.';
  }
  return null;
}

export function ChangePasswordScreen({ navigation }: Props) {
  const auth = useAuth();
  const palette = useAppColors();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const themed = useMemo(
    () => ({
      error: { ...typography.caption, color: palette.error },
      success: { ...typography.caption, color: palette.success },
    }),
    [palette],
  );

  async function submit() {
    const validation = validateNewPassword(newPassword);
    if (!currentPassword) return setError('Current password is required.');
    if (validation) return setError(validation);
    if (newPassword !== confirmPassword) return setError('Passwords do not match.');
    if (newPassword === currentPassword) return setError('Choose a different new password.');
    setSubmitting(true);
    setError('');
    setMessage('');
    try {
      await auth.changePassword({ currentPassword, newPassword, confirmPassword });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setMessage('Password changed successfully.');
    } catch (caught) {
      const apiError = caught as ApiError;
      const code = apiError.code;
      if (code === 'CURRENT_PASSWORD_INCORRECT') {
        setError('Current password is incorrect.');
      } else if (code === 'PASSWORD_REUSED') {
        setError('Choose a password you have not used recently.');
      } else if (code === 'PASSWORD_CHANGE_NOT_ALLOWED') {
        setError('Password change is not allowed for this account.');
      } else if (apiError.message && !/sql|stack|procedure/i.test(apiError.message)) {
        setError(apiError.message);
      } else {
        setError('Unable to change password. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AppScreen webLayout="narrow" scroll keyboardSafe>
      <ScreenHeader
        title="Change password"
        onBack={navigation.goBack}
      />
      <View style={styles.body}>
      <AppCard style={styles.card}>
        <AppInput
          label="Current password"
          value={currentPassword}
          onChangeText={setCurrentPassword}
          secureTextEntry
          autoCorrect={false}
          textContentType="password"
        />
        <AppInput
          label="New password"
          value={newPassword}
          onChangeText={setNewPassword}
          secureTextEntry
          autoCorrect={false}
          textContentType="newPassword"
        />
        <AppInput
          label="Confirm new password"
          value={confirmPassword}
          onChangeText={setConfirmPassword}
          secureTextEntry
          autoCorrect={false}
          textContentType="newPassword"
        />
        {error ? <Text accessibilityRole="alert" style={themed.error}>{error}</Text> : null}
        {message ? <Text accessibilityRole="alert" style={themed.success}>{message}</Text> : null}
        <AppButton title="Change password" onPress={submit} loading={submitting} />
      </AppCard>
      </View>
    </AppScreen>
  );
}

const styles = StyleSheet.create({
  body: { marginTop: spacing.lg },
  card: { gap: spacing.lg },
});
